// ./src/components/features/EventSetup/EventSetupSteps.tsx
//
// ROADMAP #161 b + d — what the host still has to do for THIS event, shown on its dashboard and on the wizard's last
// screen until it is done: card payments for an event that sells something paid, and — for a venue account — saving
// its venue as the default so the next event starts there. Home lists the first across every event (the API's
// `card_payments_off` attention kind). ⓘ No "no venue yet" step: see `setupStepRules.ts`.
//
// ⚠ Each step is derived from what is true NOW (`setupStepRules.ts`) and disappears once it is done — the visible
//   change is the confirmation, there is no toast.
// ⚠ The OWNER's steps: crew cannot connect the owner's Stripe or change the account's defaults, so they see nothing.
// ⚠ A read that fails says nothing (like `ComplianceSetupAlert`): a dashboard nudge must never break the dashboard, and
//   Home reports the same facts with its own "could not check".
// #198 c — and "try a test purchase" before the first sale: an event that sells online and has not started selling for
//   real (a draft, or in test mode), until a test record exists (`testPurchaseStep`).
import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, Card, Group, Stack, Text } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { OP_RESULT_CODES } from '@sdflc/api-helpers';

import { useApp, useOptionalAccountPreferenceContext } from '@/contexts';
import { useAccountType } from '@/hooks';
import { Event, EventService, Venue, VenueService } from '@/utils/api';
import { buildEventPublicUrl, refusalReason } from '@/utils';
import { AppIcon } from '@/components/generic';
import { CardPaymentsNotice, useCardPayments } from '@/components/features/CardPayments';

import { PAID_SALE_KIND, TEST_PURCHASE_STEP, cardPaymentsDue, offerDefaultVenue, paidSalesPhrase, testModeKinds, testPurchaseStep, testRecordTotal } from './setupStepRules';
import { useEventPaidSales } from './useEventPaidSales';
import { useEventTestRecords } from './useEventTestRecords';

export interface EventSetupStepsProps {
  event: Event;
  /** The venue's name when the screen already has it (the dashboard does); read here otherwise. */
  venueName?: string | null;
  /** This screen already shows the card-payments line (the wizard's held quick sale). */
  hideCardPayments?: boolean;
  /** Bumped by the screen's reload — the facts are read again. */
  reloadKey?: number;
  /** #198 c — the event as saved after "Turn on test mode" (null: read it again). Without it the switch is not offered. */
  onEventUpdated?: (event: Event | null) => void;
}

/** Per-browser "Not now" on the default-venue offer. A convenience, not state. */
const dismissKey = (accountId: string): string => `em.setup.defaultVenue.dismissed.${accountId}`;

const readDismissed = (accountId: string): boolean => {
  try {
    return !!accountId && window.localStorage.getItem(dismissKey(accountId)) === '1';
  } catch {
    return false;
  }
};

/** #198 c — per-browser "Not now" on the test-purchase step, per EVENT (each event is its own rehearsal). */
const testPurchaseDismissKey = (eventId: string): string => `em.setup.testPurchase.dismissed.${eventId}`;

const readTestPurchaseDismissed = (eventId: string): boolean => {
  try {
    return !!eventId && window.localStorage.getItem(testPurchaseDismissKey(eventId)) === '1';
  } catch {
    return false;
  }
};

export const EventSetupSteps: React.FC<EventSetupStepsProps> = ({ event, venueName, hideCardPayments = false, reloadKey, onEventUpdated }) => {
  const { t, i18n } = useTranslation(['events', 'common']);
  const { user } = useApp();
  const preferences = useOptionalAccountPreferenceContext();

  const ownAccountId = user?.account?.id ?? '';
  const owns = !!event?.id && !!event.accountId && event.accountId === ownAccountId;

  // ── Card payments (#161 b) ─────────────────────────────────────────────────
  // ⓘ Not read in test mode (#184): the step is never due there — the test-mode banner says what the switch needs — and
  //   read once the event goes live, so the step can appear without a reload. A failed read says nothing (`paid` null).
  // #198 c — and before the event sells for real (a draft, or in test mode), for the test-purchase step.
  const preLaunch = event.eventStatus === 'draft' || event.isTestMode === true;
  const { paid } = useEventPaidSales(event.id, owns && ((!hideCardPayments && event.isTestMode !== true) || preLaunch), reloadKey);

  const sellsPaid = !!paid && paid.kinds.length > 0 && !paid.isTestMode;
  const cardPayments = useCardPayments(sellsPaid);
  const showCards = !hideCardPayments && cardPaymentsDue(paid, cardPayments);

  const sold = useMemo(
    () =>
      paidSalesPhrase(
        paid?.kinds ?? [],
        {
          [PAID_SALE_KIND.TICKETS]: t('events:setup.sells.tickets', { defaultValue: 'tickets' }),
          [PAID_SALE_KIND.SIGNUP_ITEMS]: t('events:setup.sells.signupItems', { defaultValue: 'sign-up items' }),
          [PAID_SALE_KIND.SPONSORSHIPS]: t('events:setup.sells.sponsorships', { defaultValue: 'sponsorships' }),
        },
        i18n.language,
      ),
    [paid, t, i18n.language],
  );

  // ── A test purchase first (#198 c) ─────────────────────────────────────────
  const [testDismissed, setTestDismissed] = useState<boolean>(() => readTestPurchaseDismissed(event.id ?? ''));
  const [switching, setSwitching] = useState(false);
  const [switchError, setSwitchError] = useState<string | null>(null);
  // Counted only for an event the step could be due on — most dashboards never ask.
  const mayRehearse = owns && preLaunch && !testDismissed && !!paid && testModeKinds(paid.kinds).length > 0;
  const { records: testRecords, settled: testRecordsSettled } = useEventTestRecords(event.id, mayRehearse, reloadKey);
  const testStep = testPurchaseStep({
    owns,
    eventStatus: event.eventStatus,
    isTestMode: event.isTestMode,
    paid,
    // ⚠ A failed count is unknown (null) — not "nothing bought yet".
    testRecords: testRecordsSettled && testRecords ? testRecordTotal(testRecords) : null,
    dismissed: testDismissed,
  });
  const publicUrl = event.eventStatus === 'published' ? buildEventPublicUrl(event) : null;

  const turnOnTestMode = async () => {
    setSwitching(true);
    setSwitchError(null);

    const result = await EventService.update({ id: event.id as string }, { isTestMode: true });

    setSwitching(false);

    if (!result.didSucceed()) {
      setSwitchError(refusalReason(result) || t('events:setup.testPurchase.switchFailed', { defaultValue: 'Could not turn on test mode. Please try again.' }));

      return;
    }

    onEventUpdated?.((result.getDataFirst() as Event | undefined) ?? null);
  };

  const testNotNow = () => {
    setTestDismissed(true);

    try {
      window.localStorage.setItem(testPurchaseDismissKey(event.id ?? ''), '1');
    } catch {
      // A browser that refuses storage simply offers it again next time.
    }
  };

  // ── The default venue (#161 d) ─────────────────────────────────────────────
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(ownAccountId));
  const [venueLabel, setVenueLabel] = useState<string | null>(venueName ?? null);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Asked only when the rest of the offer holds — most hosts never pay for this read.
  const mayOffer = owns && !dismissed && !!event.venueId && !!preferences?.isLoaded && !preferences?.preference?.defaultVenueId;

  // The shared read (`useAccountType`): unknown until it answers, and a failed read stays unknown — no offer.
  const { accountType } = useAccountType(ownAccountId, mayOffer);

  const showDefaultVenue = offerDefaultVenue({
    accountType: owns ? accountType : null,
    preferenceLoaded: !!preferences?.isLoaded,
    defaultVenueId: preferences?.preference?.defaultVenueId ?? null,
    event,
    dismissed,
  });

  useEffect(() => {
    if (venueName) {
      setVenueLabel(venueName);

      return;
    }

    if (!showDefaultVenue || !event.venueId) {
      return;
    }

    let alive = true;

    void VenueService.get(event.venueId)
      .then((res) => {
        const venue = res.code === OP_RESULT_CODES.OK ? ((res.getDataFirst() as Venue | undefined) ?? null) : null;

        if (alive && venue?.name) {
          setVenueLabel(venue.name);
        }
      })
      .catch(() => undefined);

    return () => {
      alive = false;
    };
  }, [venueName, showDefaultVenue, event.venueId]);

  const saveDefaultVenue = async () => {
    if (!preferences || !event.venueId) {
      return;
    }

    setSaveError(null);

    // ⚠ Only this one key — the preferences save is a patch, and everything else the host set stays as it is.
    // ⚠ The refusal comes from THIS save's result — `preferences.lastError` here is the render before the click.
    const result = await preferences.save({ defaultVenueId: event.venueId });

    if (result.code !== OP_RESULT_CODES.OK) {
      setSaveError(
        refusalReason(result) || t('events:setup.defaultVenue.failed', { defaultValue: 'Could not save your venue. Please try again.' }),
      );
    }
  };

  const notNow = () => {
    setDismissed(true);

    try {
      window.localStorage.setItem(dismissKey(ownAccountId), '1');
    } catch {
      // A browser that refuses storage simply offers it again next time.
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  const showTestPurchase = testStep === TEST_PURCHASE_STEP.BUY || (testStep === TEST_PURCHASE_STEP.TURN_ON && !!onEventUpdated);

  if (!owns || (!showCards && !showDefaultVenue && !showTestPurchase)) {
    return null;
  }

  const testCard = t('events:setup.testPurchase.card', { defaultValue: '4242 4242 4242 4242 (any future date, any CVC)' });
  const testWhat = paidSalesPhrase(
    testModeKinds(paid?.kinds),
    {
      [PAID_SALE_KIND.TICKETS]: t('events:setup.sells.tickets', { defaultValue: 'tickets' }),
      [PAID_SALE_KIND.SIGNUP_ITEMS]: t('events:setup.sells.signupItems', { defaultValue: 'sign-up items' }),
      [PAID_SALE_KIND.SPONSORSHIPS]: t('events:setup.sells.sponsorships', { defaultValue: 'sponsorships' }),
    },
    i18n.language,
  );

  const cardsNotice = (
    <CardPaymentsNotice
      payments={cardPayments}
      paid
      offBody={t('events:setup.cardsOff', {
        defaultValue:
          'This event sells {{what}}, and buyers cannot pay for them yet. Connect your Stripe account — the money goes straight to you — then check again.',
        what: sold || t('events:setup.sells.something', { defaultValue: 'something that costs money' }),
      })}
    />
  );

  return (
    <Stack gap="md" w="100%" ta="left">
      {showTestPurchase ? (
        <Card withBorder radius="md" padding="md">
          <Stack gap="xs" align="flex-start">
            <Group gap="sm" wrap="nowrap" align="flex-start">
              <Box pt={2} style={{ flexShrink: 0 }}>
                <AppIcon iconName="CreditCard" size={20} />
              </Box>
              <Text size="md" fw={600}>
                {t('events:setup.testPurchase.title', { defaultValue: 'Try a test purchase before your first sale' })}
              </Text>
            </Group>
            <Text size="md" c="dimmed">
              {testStep === TEST_PURCHASE_STEP.TURN_ON
                ? t('events:setup.testPurchase.turnOnBody', {
                    defaultValue:
                      'Turn on test mode and buy {{what}} yourself, as a guest would: the checkout, the emails and the receipt are real, nothing is charged, and the test records are hidden when you switch to live payments.',
                    what: testWhat,
                  })
                : event.eventStatus === 'draft'
                  ? t('events:setup.testPurchase.buyDraftBody', {
                      defaultValue:
                        'Test mode is on, so only test cards are taken. Publish the event, then buy {{what}} on its page with the test card {{card}}.',
                      what: testWhat,
                      card: testCard,
                    })
                  : t('events:setup.testPurchase.buyBody', {
                      defaultValue: 'Test mode is on. Buy {{what}} on the event\'s page with the test card {{card}}.',
                      what: testWhat,
                      card: testCard,
                    })}
            </Text>
            {switchError ? (
              <Text size="md" c="red">
                {switchError}
              </Text>
            ) : null}
            <Group gap="sm">
              {testStep === TEST_PURCHASE_STEP.TURN_ON ? (
                <Button size="md" variant="light" loading={switching} onClick={() => void turnOnTestMode()}>
                  {t('events:setup.testPurchase.turnOn', { defaultValue: 'Turn on test mode' })}
                </Button>
              ) : publicUrl ? (
                <Button
                  size="md"
                  variant="light"
                  component="a"
                  href={publicUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  leftSection={<AppIcon iconName="ExternalLink" size={16} />}
                >
                  {t('events:setup.testPurchase.open', { defaultValue: 'Open the event page' })}
                </Button>
              ) : null}
              <Button size="md" variant="subtle" color="gray" onClick={testNotNow} disabled={switching}>
                {t('events:setup.testPurchase.notNow', { defaultValue: 'Not now' })}
              </Button>
            </Group>
          </Stack>
        </Card>
      ) : null}

      {/* "Not set up" is an Alert of its own; only the plain "could not check" line needs a frame around it. */}
      {showCards && cardPayments.status ? cardsNotice : null}
      {showCards && !cardPayments.status ? (
        <Card withBorder radius="md" padding="md">
          {cardsNotice}
        </Card>
      ) : null}

      {showDefaultVenue ? (
        <Card withBorder radius="md" padding="md">
          <Stack gap="xs" align="flex-start">
            <Group gap="sm" wrap="nowrap" align="flex-start">
              {/* Top-aligned and nudged onto the first line's centre (20px icon, ~25px line), so a wrapped title keeps it. */}
              <Box pt={2} style={{ flexShrink: 0 }}>
                <AppIcon iconName="Building" size={20} />
              </Box>
              <Text size="md" fw={600}>
                {venueLabel
                  ? t('events:setup.defaultVenue.title', { defaultValue: 'Start every new event at {{venue}}?', venue: venueLabel })
                  : t('events:setup.defaultVenue.titleNoName', { defaultValue: 'Start every new event at this venue?' })}
              </Text>
            </Group>
            <Text size="md" c="dimmed">
              {t('events:setup.defaultVenue.body', {
                defaultValue: 'Save it as your venue, and new events are set there from the start. You can change it in Event defaults.',
              })}
            </Text>
            {saveError ? (
              <Text size="md" c="red">
                {saveError}
              </Text>
            ) : null}
            <Group gap="sm">
              <Button size="md" loading={!!preferences?.isSaving} onClick={() => void saveDefaultVenue()}>
                {t('events:setup.defaultVenue.save', { defaultValue: 'Make it my venue' })}
              </Button>
              <Button size="md" variant="subtle" color="gray" disabled={!!preferences?.isSaving} onClick={notNow}>
                {t('common:notNow', { defaultValue: 'Not now' })}
              </Button>
            </Group>
          </Stack>
        </Card>
      ) : null}
    </Stack>
  );
};
